// ============================================================================
// useModelPhotos — the ONE way a screen gets a model's picture (system-wide)
// ============================================================================
// useModelPhotos(itemNos, devOf?) → { get(item), isError, isLoading }
//   get(item) → { state: 'loading' }
//             | { state: 'ok', source: 'library'|'sales'|'dev'|'sample', path? , src?, barcode? }
//                 path = a files-bank photo (draw it with ModelPhotoImg: a thumbnail, lazily, never the original)
//                 src  = a public picture url (Sales master, dev card)
//             | { state: 'none' }    every source answered and none has a picture
//             | { state: 'error' }   a source failed to read, so "none" cannot be claimed
// devOf = optional { itemNo: devNo }; it switches on the dev-card and sample-sheet sources for those items.
// The order and every reader live in utils/modelPhotoSources.js. Nothing is signed here: a files-bank photo is read
// with the session's own login by the thumbnail loader (files/data/thumbs.js), so there is no URL to expire.
// A read that failed in part is kept only briefly (the screen's Refresh invalidates MODEL_PHOTOS_KEY too).
// ============================================================================
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { photoCodes } from '../utils/modelPhotoPick';
import { resolveModelPhotoSources } from '../utils/modelPhotoSources';

export const MODEL_PHOTOS_KEY = ['model-photos'];
const FRESH = 10 * 60 * 1000;
const FRESH_UNSURE = 15 * 1000;

export async function loadModelPhotos(codes, devOf) {
  const { sources, failed } = await resolveModelPhotoSources(codes.map((c) => ({ item: c, dev: devOf ? devOf[c] : undefined })));
  const map = new Map();
  sources.forEach((s, i) => { if (s) map.set(codes[i], s.path ? { path: s.path, barcode: null, source: s.source } : { src: s.url, barcode: s.barcode || null, source: s.source }); });
  return { map, unsure: !!(failed.library || failed.sales || failed.dev || failed.sample) };
}

export default function useModelPhotos(itemNos, devOf) {
  const codes = useMemo(() => photoCodes(itemNos), [itemNos]);
  const devKey = devOf ? codes.map((c) => devOf[c] || '').join('|') : '';
  const q = useQuery({
    queryKey: [...MODEL_PHOTOS_KEY, codes.join('|'), devKey],
    enabled: codes.length > 0,
    staleTime: (query) => (query.state.data && query.state.data.unsure ? FRESH_UNSURE : FRESH),
    retry: 1, refetchOnWindowFocus: false,
    queryFn: () => loadModelPhotos(codes, devOf),
  });
  const data = q.data || null;
  const get = useCallback((item) => {
    if (!item) return { state: 'none' };
    if (!data) return { state: q.isError ? 'error' : 'loading' };   // pictures already read stay shown if a later refresh fails
    const p = data.map.get(item);
    if (p) return { state: 'ok', ...p };
    return { state: data.unsure ? 'error' : 'none' };
  }, [data, q.isError]);
  return { get, isError: q.isError && !data, isLoading: codes.length > 0 && q.isLoading };
}
